import { useState, useCallback } from 'react';
import * as Slider from '@radix-ui/react-slider';

interface RangeSliderProps {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue?: (value: number) => string;
  label?: string;
  gradient?: string;
}

export function RangeSlider({
  min,
  max,
  step = 1,
  value,
  onChange,
  formatValue = (v) => v.toString(),
  label,
  gradient = 'from-cyan-500 to-teal-500',
}: RangeSliderProps) {
  const [activeThumb, setActiveThumb] = useState<number | null>(null);

  const handleValueChange = useCallback((newValue: number[]) => {
    onChange([newValue[0], newValue[1]]);
  }, [onChange]);

  return (
    <div className="space-y-3">
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-300">{label}</span>
          <span className="text-sm font-medium text-white bg-slate-700/50 px-3 py-1 rounded-full">
            {formatValue(value[0])} — {formatValue(value[1])}
          </span>
        </div>
      )}

      <div className="relative pt-4 pb-2 px-2">
        <Slider.Root
          className="relative flex items-center select-none touch-none w-full h-6 cursor-pointer"
          value={value}
          onValueChange={handleValueChange}
          min={min}
          max={max}
          step={step}
          minStepsBetweenThumbs={1}
        >
          {/* Track background with subtle gradient */}
          <Slider.Track className="bg-gradient-to-r from-slate-700 to-slate-600 relative grow rounded-full h-2 overflow-hidden shadow-inner">
            {/* Active range with gradient fill */}
            <Slider.Range
              className={`absolute h-full rounded-full bg-gradient-to-r ${gradient}
                         shadow-[0_0_10px_rgba(0,200,200,0.3)] transition-shadow duration-300`}
            />
          </Slider.Track>

          {/* Min thumb */}
          <Slider.Thumb
            className="group block w-6 h-6 bg-white rounded-full
                       shadow-[0_2px_10px_rgba(0,0,0,0.3)]
                       hover:shadow-[0_0_20px_rgba(0,200,200,0.5)]
                       focus:outline-none focus:shadow-[0_0_0_3px_rgba(0,200,200,0.3)]
                       transition-all duration-200 ease-out
                       hover:scale-110 active:scale-95
                       cursor-grab active:cursor-grabbing"
            onPointerDown={() => setActiveThumb(0)}
            onPointerUp={() => setActiveThumb(null)}
            onBlur={() => setActiveThumb(null)}
          >
            {/* Inner dot */}
            <div className={`absolute inset-1.5 rounded-full bg-gradient-to-br ${gradient}
                            transition-transform duration-200 group-hover:scale-110`} />

            {/* Tooltip */}
            <div
              className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-3
                         px-2.5 py-1.5 bg-[#1A1F26] text-white text-xs font-semibold rounded-lg
                         shadow-xl ring-1 ring-white/[0.08]
                         transition-all duration-200 ease-out pointer-events-none whitespace-nowrap
                         ${activeThumb === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
            >
              {formatValue(value[0])}
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                <div className="border-[6px] border-transparent border-t-slate-800" />
              </div>
            </div>
          </Slider.Thumb>

          {/* Max thumb */}
          <Slider.Thumb
            className="group block w-6 h-6 bg-white rounded-full
                       shadow-[0_2px_10px_rgba(0,0,0,0.3)]
                       hover:shadow-[0_0_20px_rgba(0,200,200,0.5)]
                       focus:outline-none focus:shadow-[0_0_0_3px_rgba(0,200,200,0.3)]
                       transition-all duration-200 ease-out
                       hover:scale-110 active:scale-95
                       cursor-grab active:cursor-grabbing"
            onPointerDown={() => setActiveThumb(1)}
            onPointerUp={() => setActiveThumb(null)}
            onBlur={() => setActiveThumb(null)}
          >
            {/* Inner dot */}
            <div className={`absolute inset-1.5 rounded-full bg-gradient-to-br ${gradient}
                            transition-transform duration-200 group-hover:scale-110`} />

            {/* Tooltip */}
            <div
              className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-3
                         px-2.5 py-1.5 bg-[#1A1F26] text-white text-xs font-semibold rounded-lg
                         shadow-xl ring-1 ring-white/[0.08]
                         transition-all duration-200 ease-out pointer-events-none whitespace-nowrap
                         ${activeThumb === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
            >
              {formatValue(value[1])}
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                <div className="border-[6px] border-transparent border-t-slate-800" />
              </div>
            </div>
          </Slider.Thumb>
        </Slider.Root>
      </div>
    </div>
  );
}

// Modern preset button with glass effect and animations
interface PresetButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  color?: 'cyan' | 'yellow' | 'red' | 'purple' | 'green';
}

export function PresetButton({ label, isActive, onClick, color = 'cyan' }: PresetButtonProps) {
  const baseClasses = `
    relative overflow-hidden
    px-4 py-2 rounded-full text-sm font-medium
    transition-all duration-300 ease-out
    hover:scale-105 active:scale-95
    backdrop-blur-sm
  `;

  const colorMap = {
    cyan: {
      active: 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg shadow-cyan-500/25',
      inactive: 'bg-[#1A1F26]/80 text-slate-300 border border-slate-600/50 hover:border-cyan-500/50 hover:text-cyan-300',
    },
    yellow: {
      active: 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow-lg shadow-yellow-500/25',
      inactive: 'bg-[#1A1F26]/80 text-slate-300 border border-slate-600/50 hover:border-yellow-500/50 hover:text-yellow-300',
    },
    red: {
      active: 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg shadow-red-500/25',
      inactive: 'bg-[#1A1F26]/80 text-slate-300 border border-slate-600/50 hover:border-red-500/50 hover:text-red-300',
    },
    purple: {
      active: 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/25',
      inactive: 'bg-[#1A1F26]/80 text-slate-300 border border-slate-600/50 hover:border-purple-500/50 hover:text-purple-300',
    },
    green: {
      active: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/25',
      inactive: 'bg-[#1A1F26]/80 text-slate-300 border border-slate-600/50 hover:border-green-500/50 hover:text-green-300',
    },
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${isActive ? colorMap[color].active : colorMap[color].inactive}`}
    >
      {/* Glass shine effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />

      {/* Pulse ring on active */}
      {isActive && (
        <span className="absolute inset-0 rounded-full animate-ping-once bg-white/20" />
      )}

      <span className="relative z-10">{label}</span>
    </button>
  );
}
